/**
 * Shared validation utilities
 */

export type TransactionType = 'debit' | 'credit' | 'savings';
export type BudgetKind = 'income' | 'expense';

export interface Transaction {
  amount: number;
  type: TransactionType;
  description: string;
  category: string;
  date?: string | Date; // Optional ISO 8601 formatted date or Date object
  goal?: string; // Optional goal ID for savings transactions
}

export interface UpdateTransactionRequest {
  amount?: number;
  type?: TransactionType;
  description?: string;
  category?: string;
  goal?: string;
}

export interface BudgetItem {
  name: string;
  amount: number;
  kind: BudgetKind;
  currency: string;
}

function isValidTransactionType(type: string): type is TransactionType {
  return type === 'debit' || type === 'credit' || type === 'savings';
}

function isValidBudgetKind(kind: string): kind is BudgetKind {
  return kind === 'income' || kind === 'expense';
}

export function validateTransaction(transaction: Transaction): void {
  if (!transaction?.amount || !transaction?.type || !transaction?.description) {
    throw new Error('Transaction must have amount, type, and description');
  }
  
  if (!isValidTransactionType(transaction.type)) {
    throw new Error('Transaction type must be "debit", "credit", or "savings"');
  }
  
  if (typeof transaction.amount !== 'number' || transaction.amount <= 0) {
    throw new Error('Transaction amount must be a positive number');
  }
}

export function validateUpdateTransactionRequest(request: UpdateTransactionRequest): void {
  if (!request || Object.keys(request).length === 0) {
    throw new Error('Update request must contain at least one field to update');
  }
  
  if (request.amount !== undefined) {
    if (typeof request.amount !== 'number' || request.amount <= 0) {
      throw new Error('Transaction amount must be a positive number');
    }
    if (request.amount > 999999999.99) {
      throw new Error('Transaction amount cannot exceed 999999999.99');
    }
  }
  
  if (request.type !== undefined) {
    if (!isValidTransactionType(request.type)) {
      throw new Error('Transaction type must be "debit", "credit", or "savings"');
    }
  }
  
  if (request.description !== undefined) {
    if (typeof request.description !== 'string' || request.description.trim().length === 0) {
      throw new Error('Transaction description must be a non-empty string');
    }
    if (request.description.length > 500) {
      throw new Error('Transaction description cannot exceed 500 characters');
    }
  }
  
  if (request.category !== undefined) {
    if (typeof request.category !== 'string' || request.category.trim().length === 0) {
      throw new Error('Transaction category must be a non-empty string');
    }
    if (request.category.length > 100) {
      throw new Error('Transaction category cannot exceed 100 characters');
    }
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(request.category)) {
      throw new Error('Transaction category can only contain letters, numbers, spaces, hyphens, and underscores');
    }
  }
  
  if (request.goal !== undefined) {
    if (typeof request.goal !== 'string') {
      throw new Error('Transaction goal must be a string');
    }
    if (request.goal.length > 100) {
      throw new Error('Transaction goal cannot exceed 100 characters');
    }
    if (request.goal.length > 0 && !/^[a-zA-Z0-9\-_]*$/.test(request.goal)) {
      throw new Error('Transaction goal can only contain letters, numbers, hyphens, and underscores');
    }
  }
}

export function validateBudgetItem(item: BudgetItem): void {
  if (!item?.name || !item?.amount || !item?.kind || !item?.currency) {
    throw new Error('Budget item must have name, amount, kind, and currency');
  }
  
  if (typeof item.name !== 'string' || item.name.trim().length === 0) {
    throw new Error('Budget item name must be a non-empty string');
  }
  
  if (typeof item.amount !== 'number' || item.amount < 0) {
    throw new Error('Budget item amount must be a non-negative number');
  }
  
  if (!isValidBudgetKind(item.kind)) {
    throw new Error('Budget item kind must be "income" or "expense"');
  }
  
  if (typeof item.currency !== 'string' || !/^[A-Z]{3}$/.test(item.currency)) {
    throw new Error('Budget item currency must be a 3-letter ISO code (e.g., USD)');
  }
}

export function validateBudgetItems(items: BudgetItem[]): void {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Budget items must be a non-empty array');
  }
  
  if (items.length > 100) {
    throw new Error('Budget cannot have more than 100 items');
  }
  
  items.forEach((item, index) => {
    try {
      validateBudgetItem(item);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown validation error';
      throw new Error(`Budget item ${index + 1}: ${errorMessage}`);
    }
  });
}

export function validateCategoryName(categoryName: unknown): asserts categoryName is string {
  if (typeof categoryName !== 'string' || categoryName.trim().length === 0) {
    throw new Error('Category name must be a non-empty string');
  }
  
  if (categoryName.length > 100) {
    throw new Error('Category name cannot exceed 100 characters');
  }
}

export function validateAccessToken(token: unknown): asserts token is string {
  if (!token || typeof token !== 'string') {
    throw new Error('Access token is required and must be a string');
  }
}

export function validateMonth(month: unknown): asserts month is string {
  const MONTH_REGEX = /^\d{4}-\d{2}$/;
  if (typeof month !== 'string' || !MONTH_REGEX.test(month)) {
    throw new Error('Month must be in YYYY-MM format');
  }
}

export function validateTransactionId(transactionId: unknown): asserts transactionId is string {
  const TRANSACTION_ID_REGEX = /^TXN#\d{4}-\d{2}#\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z#\d+$/;
  if (typeof transactionId !== 'string' || !TRANSACTION_ID_REGEX.test(transactionId)) {
    throw new Error('Transaction ID must be in format: TXN#YYYY-MM#ISO8601#timestamp');
  }
}

export function validateTransactionParseRequest(description: unknown, referenceDate?: unknown): asserts description is string {
  if (typeof description !== 'string' || description.trim().length === 0) {
    throw new Error('Transaction description must be a non-empty string');
  }
  
  if (description.length > 2000) {
    throw new Error('Transaction description cannot exceed 2000 characters');
  }
  
  // Validate referenceDate if provided
  if (referenceDate !== undefined) {
    if (typeof referenceDate !== 'string') {
      throw new Error('Reference date must be a string in ISO 8601 format');
    }
    
    // Validate ISO 8601 format
    const date = new Date(referenceDate);
    if (isNaN(date.getTime())) {
      throw new Error('Reference date must be a valid ISO 8601 formatted date');
    }
  }
}

// Savings goals validation interfaces and functions
export interface CreateSavingsGoalRequest {
  name: string;
  targetAmount: number;
  duration: number;
}

export interface UpdateSavingsGoalRequest {
  name?: string;
  targetAmount?: number;
  duration?: number;
  status?: 'active' | 'completed' | 'paused';
  actualProgress?: number;
  pausedDate?: string;
  resumedDate?: string;
  totalPausedDays?: number;
}

function isValidGoalStatus(status: string): status is 'active' | 'completed' | 'paused' {
  return status === 'active' || status === 'completed' || status === 'paused';
}

export function validateCreateGoalRequest(request: CreateSavingsGoalRequest): void {
  if (!request?.name || !request?.targetAmount || !request?.duration) {
    throw new Error('Goal must have name, targetAmount, and duration');
  }
  
  if (typeof request.name !== 'string' || request.name.trim().length === 0) {
    throw new Error('Goal name must be a non-empty string');
  }
  
  if (request.name.length > 50) {
    throw new Error('Goal name cannot exceed 50 characters');
  }
  
  if (typeof request.targetAmount !== 'number' || request.targetAmount <= 0) {
    throw new Error('Goal target amount must be a positive number');
  }
  
  if (typeof request.duration !== 'number' || request.duration < 1 || request.duration > 365) {
    throw new Error('Goal duration must be between 1 and 365 days');
  }
}

export function validateUpdateGoalRequest(request: UpdateSavingsGoalRequest): void {
  if (!request || Object.keys(request).length === 0) {
    throw new Error('Update request must contain at least one field to update');
  }
  
  if (request.name !== undefined) {
    if (typeof request.name !== 'string' || request.name.trim().length === 0) {
      throw new Error('Goal name must be a non-empty string');
    }
    if (request.name.length > 50) {
      throw new Error('Goal name cannot exceed 50 characters');
    }
  }
  
  if (request.targetAmount !== undefined) {
    if (typeof request.targetAmount !== 'number' || request.targetAmount <= 0) {
      throw new Error('Goal target amount must be a positive number');
    }
  }
  
  if (request.duration !== undefined) {
    if (typeof request.duration !== 'number' || request.duration < 1 || request.duration > 365) {
      throw new Error('Goal duration must be between 1 and 365 days');
    }
  }
  
  if (request.status !== undefined) {
    if (!isValidGoalStatus(request.status)) {
      throw new Error('Goal status must be "active", "completed", or "paused"');
    }
  }
  
  if (request.actualProgress !== undefined) {
    if (typeof request.actualProgress !== 'number' || request.actualProgress < 0) {
      throw new Error('Goal actual progress must be a non-negative number');
    }
  }
  
  if (request.totalPausedDays !== undefined) {
    if (typeof request.totalPausedDays !== 'number' || request.totalPausedDays < 0) {
      throw new Error('Goal total paused days must be a non-negative number');
    }
  }
  
  // Validate date strings if provided
  if (request.pausedDate !== undefined) {
    if (typeof request.pausedDate !== 'string') {
      throw new Error('Paused date must be a string in ISO 8601 format');
    }
    const date = new Date(request.pausedDate);
    if (isNaN(date.getTime())) {
      throw new Error('Paused date must be a valid ISO 8601 formatted date');
    }
  }
  
  if (request.resumedDate !== undefined) {
    if (typeof request.resumedDate !== 'string') {
      throw new Error('Resumed date must be a string in ISO 8601 format');
    }
    const date = new Date(request.resumedDate);
    if (isNaN(date.getTime())) {
      throw new Error('Resumed date must be a valid ISO 8601 formatted date');
    }
  }
}

export function validateGoalId(goalId: unknown): asserts goalId is string {
  if (typeof goalId !== 'string' || goalId.trim().length === 0) {
    throw new Error('Goal ID must be a non-empty string');
  }
}